<script>
import { minLength, maxLength, email, requiredIf } from 'vuelidate/lib/validators'
import AuthTemplate from '@/components/authentication/AuthTemplate'
import InputField from '@/components/general/InputField'
import Loading from '@/components/general/Loading'
import { applySsoHandoff, getSsoLoginUrl } from '@/support/sso'

export default {
  name: 'CorporateLoginPage',
  props: {
    isAttemptingLogin: {
      type: Boolean,
      default: false
    },
    pwOrEmailInvalid: {
      type: Boolean,
      default: false
    },
    switchTarget: {
      type: String,
      default: '/login'
    },
    switchLabel: {
      type: String,
      default: ''
    }
  },
  components: {
    AuthTemplate,
    InputField,
    Loading
  },
  validations: {
    formData: {
      email: {
        required: requiredIf(function () {
          return !this.ssoEnabled
        }),
        email,
        minLength: minLength(3),
        maxLength: maxLength(160),
        isEnliztEmail: function (val) {
          if (!this.formData.emailResponse) return true
          if (this.formData.emailResponse.status === 400 || this.formData.emailResponse.status === 403 || this.formData.emailResponse.status === 404 || (['enlizt.com', 'enlizt.com.br', 'enlizt.br'].includes(val.split('@')[1]))) return false
          return true
        }
      },
      password: {
        required: requiredIf(function () {
          return !this.ssoEnabled
        }),
        passwordOrEmailInvalid: {
          value () {
            return !this.pwOrEmailInvalid
          }
        }
      }
    }
  },
  data () {
    return {
      formData: {
        email: null,
        password: null,
        emailResponse: null
      },
      ssoEnabled: false,
      ssoCompanyName: null,
      ssoQueryLoading: false
    }
  },
  computed: {
    ssoQuerySubdomain () {
      if (this.$route.query.token) return null
      const value = this.$route.query.sso
      if (typeof value !== 'string') return null
      const normalized = value.trim().toLowerCase()
      if (!normalized || normalized === 'handoff') return null
      return normalized
    },
    ssoDisplayName () {
      return this.ssoCompanyName || this.ssoQuerySubdomain || ''
    },
    ssoLoginUrl () {
      return getSsoLoginUrl()
    },
    pageTitle () {
      if (this.ssoEnabled && this.ssoDisplayName) {
        return this.ssoDisplayName
      }
      return this.$t('login.corporate:title')
    },
    pageSubtitle () {
      if (this.ssoEnabled) {
        return this.$t('login.sso:subtitle')
      }
      return this.$t('login.corporate:subtitle')
    },
    formTitle () {
      if (this.ssoEnabled) {
        return this.$t('login.sso:form.title', { company: this.ssoDisplayName })
      }
      return this.$t('login.corporate:form.title')
    },
    submitLabel () {
      if (this.isAttemptingLogin) return this.$t('global:wait')
      if (this.ssoEnabled) {
        return this.$t('login.sso:submit', { company: this.ssoDisplayName })
      }
      return this.$t('login.form:submit')
    }
  },
  methods: {
    submit () {
      this.$v.formData.$touch()
      if (this.$v.formData.$invalid) return
      this.$emit('submit', this.formData)
    },
    discoverSso (input) {
      this.$store.dispatch('attemptDiscoverSso', { email: input })
        .then((data) => {
          this.ssoEnabled = data.ssoEnabled === true
          this.ssoCompanyName = data.companyName || null
          if (this.ssoEnabled) {
            this.$nextTick(() => this.$v.formData.$reset())
          }
        })
        .catch(() => {
          this.ssoEnabled = false
          this.ssoCompanyName = null
        })
    },
    discoverSsoByQuery () {
      const subdomain = this.ssoQuerySubdomain
      if (!subdomain) return

      this.ssoQueryLoading = true
      this.ssoEnabled = false
      this.ssoCompanyName = null

      this.$store.dispatch('attemptDiscoverSso', { subdomain })
        .then((data) => {
          this.ssoEnabled = data.ssoEnabled === true
          this.ssoCompanyName = data.companyName || subdomain

          if (!this.ssoEnabled) {
            this.$router.replace({ name: 'auth.login.corporate' })
            return
          }

          this.$nextTick(() => this.$v.formData.$reset())
        })
        .catch(() => {
          this.$router.replace({ name: 'auth.login.corporate' })
        })
        .finally(() => {
          this.ssoQueryLoading = false
        })
    }
  },
  created () {
    applySsoHandoff({
      token: this.$route.query.token,
      subdomain: this.$route.query.subdomain
    })
  },
  watch: {
    'formData.email' (input) {
      if (this.ssoQuerySubdomain) return

      if (input && email(input) && input.length > 0) {
        this.debounceEvent(async () => {
          try {
            this.formData.emailResponse = await this.$store.dispatch('attemptVerifyEmail', input)
          } catch (err) {
            this.formData.emailResponse = err.response
          }
          this.discoverSso(input)
        }, 400)
      } else {
        this.ssoEnabled = false
        this.ssoCompanyName = null
      }
    },
    ssoQuerySubdomain: {
      immediate: true,
      handler (value) {
        if (value) {
          this.discoverSsoByQuery()
        }
      }
    }
  }
}
</script>
<template>
  <auth-template
    color="dark-blue"
    :title="pageTitle"
    :subtitle="pageSubtitle"
    logo="/assets/images/dark-yellow-logo.png"
  >
    <template #image>
      <img src="/assets/images/customers/plooral-handshake.png" height="100%" />
    </template>
    <template #form>
      <loading v-if="ssoQueryLoading" />
      <div v-else class="login--container">
        <v-card class="login-form--wrapper">
          <h4>{{ formTitle }}</h4>
          <p v-if="ssoEnabled" class="login-form-subtitle">{{ $t('login.sso:form.description') }}</p>
          <div v-else class="login-form-subtitle"></div>
          <input-field
            v-if="!ssoEnabled"
            :disabled="isAttemptingLogin"
            type="email"
            autocomplete="username"
            class="mt-10"
            outlined
            v-model="formData.email"
            :label="$t('registration.form:email')"
            :validation="$v.formData.email"
            :hide-validation="!$v.formData.email.$error && isMobile"
            @keydown.enter.prevent="submit"
            @click="$emit('disableErrorPw')"
          />
          <input-field
            v-if="!ssoEnabled"
            :disabled="isAttemptingLogin"
            type="password"
            autocomplete="current-password"
            outlined
            v-model="formData.password"
            :label="$t('login.form:password')"
            :validation="$v.formData.password"
            :hide-validation="!$v.formData.password.$error && isMobile"
            @keydown.enter="submit()"
            @click="$emit('disableErrorPw')"
          />
          <router-link v-if="!ssoEnabled" class="login-form-subtitle" to="/recovery">
            {{ $t('login.form:recover') }}
          </router-link>
          <v-btn
            v-if="ssoEnabled"
            :href="ssoLoginUrl"
            tag="a"
            class="mt-10 btn bold transform-unset"
            color="#1200D3"
            style="color: white; font-size: 16px;"
            large
            block
          >
            {{ submitLabel }}
          </v-btn>
          <v-btn
            v-else
            class="mt-10 btn bold transform-unset"
            color="#1200D3"
            style="color: white; font-size: 16px;"
            :loading="isAttemptingLogin"
            :disabled="$v.$invalid || isAttemptingLogin"
            large
            block
            @click.stop="submit()"
          >
            {{ submitLabel }}
          </v-btn>

          <div v-if="switchTarget && !ssoEnabled" class="login-switch-section">
            <v-btn
              style="color: #1200D3; font-size: 16px; border: 2px solid #1200D3;"
              class="login-switch-btn btn bold transform-unset"
              color="transparent"
              large
              block
              @click="$router.push(switchTarget)"
            >
              {{ switchLabel || $t('login.corporate:switch.candidate') }}
            </v-btn>
          </div>
        </v-card>
      </div>
    </template>
  </auth-template>
</template>
<style lang="scss">
  .login--container .v-input .v-label {
    color: rgba(0,0,0,.6) !important;
  }

  .login-switch-section {
    margin-top: 24px;

    .login-switch-btn {
      border: 2px solid #1200D3 !important;
      background-color: transparent !important;

      &:hover {
        background-color: rgba(18, 0, 211, 0.05) !important;
      }
    }
  }
</style>

<style lang="scss" scoped>
.login--container {
  max-width: 100%;
  width: 515px;

  .login-form--wrapper {
    padding: 45px 64px;
    border-radius: 10px;
    text-align: left;
    background: #ffffff;
    color: #000;

    h4 {
      color: #000;
    }

    .login-form-subtitle {
      @extend .subtitle--1;
      color: rgba(0, 0, 0, 0.6);

      a {
        color: #1200D3;
      }
    }
  }

  &.dark-blue {
    .auth-container--wrapper {
      .presentation--container {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .footer--container {
          margin-top: auto;
          margin-bottom: 40px;

          h5 {
            margin: 0;
            text-align: left;
          }
        }
      }
    }
  }

  @media only screen and (max-width: 768px) {
    width: 100%;
    margin: 0 auto;

    .login-form--wrapper {
      border-radius: 10px 10px 0 0;
      padding: 36px 24px 80px;
    }
  }

  @media only screen and (max-width: 1024px) {
    width: 100%;
    max-width: 515px;
  }
}
</style>
